import {
  Button,
  ButtonProps,
  CircularProgress,
  makeStyles,
  SvgIconProps,
  Theme,
  Typography,
} from '@material-ui/core';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import clsx from 'clsx';
import {
  cloneElement,
  forwardRef,
  isValidElement,
  memo,
  ReactElement,
  ReactNode,
  Ref,
} from 'react';
import { WbUnstyledLink } from '../WbLink';

export type Variant = 'text' | 'outlined' | 'contained';

export type ButtonAccessibilityProps =
  | { icon: ReactElement<SvgIconProps>; label: ReactNode }
  | { icon: ReactElement<SvgIconProps>; 'aria-label': string }
  | { icon?: never };

export type WbButtonProps = Omit<ButtonProps, 'children'> &
  ButtonAccessibilityProps & {
    iconColor?: string;
    label?: ReactNode;
    loading?: boolean;
    variant?: Variant;
    to?: string;
    fullWidthUnderBreakpoint?: Breakpoint;
  };

type UseStylesProps = {
  fullWidthUnderBreakpoint?: Breakpoint;
};

const useStyles = makeStyles<Theme, UseStylesProps>((theme: Theme) => ({
  root: {
    position: 'relative',
    whiteSpace: 'nowrap',
    '&:focus-visible': {
      // disable shadow when focused on some variants
      boxShadow: 'none',
      // override outline to be outside so that it has no contrast issues with outline
      outlineOffset: '2px',
    },
  },
  label: {
    fontWeight: 500,
  },
  text: {
    paddingInline: 0,
    minWidth: 0,
    fontWeight: 400,
    textUnderlineOffset: `${theme.spacing(0.5)}px`,
  },
  underline: {
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  onlyIcon: {
    minWidth: 0,
    borderRadius: '50%',
    padding: '4px',
  },
  fullWidthUnderBreakpoint: ({ fullWidthUnderBreakpoint }) => ({
    ...(fullWidthUnderBreakpoint && {
      [`@container (max-width: ${theme.breakpoints.values[fullWidthUnderBreakpoint]}px)`]:
        {
          width: '100%',
        },
    }),
  }),
}));

export const WbButtonComponent = (
  {
    loading = false,
    icon,
    label,
    onClick,
    color = 'primary',
    iconColor,
    variant = 'contained',
    style,
    className,
    to,
    fullWidth = false,
    fullWidthUnderBreakpoint,
    ...props
  }: WbButtonProps,
  ref: Ref<HTMLButtonElement>,
) => {
  const classes = useStyles({ fullWidthUnderBreakpoint });

  const onlyIcon = icon && !label;

  return (
    <Button
      {...(to ? { component: WbUnstyledLink, to } : {})}
      onClick={onClick}
      variant={variant}
      color={color}
      size="small"
      ref={ref}
      classes={{
        text: clsx(
          classes.text,
          !icon && !isValidElement(label) && classes.underline,
        ),
      }}
      className={clsx(
        classes.root,
        className,
        onlyIcon && classes.onlyIcon,
        fullWidthUnderBreakpoint && classes.fullWidthUnderBreakpoint,
      )}
      style={style}
      {...props}
    >
      {loading && (
        <span
          style={{
            position: 'absolute',
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <CircularProgress
            size={20}
            thickness={8}
            color="secondary"
            style={{ color: 'inherit' }}
          />
        </span>
      )}
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          visibility: loading ? 'hidden' : 'visible',
          width: fullWidth ? '100%' : 'auto',
        }}
      >
        {icon &&
          cloneElement(icon, {
            htmlColor: iconColor ?? 'inherit',
            fontSize: 'small',
          })}

        {label &&
          (isValidElement(label) ? (
            label
          ) : (
            <Typography
              component="span"
              className={clsx(
                variant === 'text' ? classes.text : classes.label,
                !icon && variant === 'text' && classes.underline,
              )}
              style={{ marginLeft: icon ? '4px' : '0px' }}
            >
              {label}
            </Typography>
          ))}
      </span>
    </Button>
  );
};

export const WbButton = memo(
  forwardRef(WbButtonComponent),
) as typeof WbButtonComponent;
